/**
 * documentModel — the ONE assembly of "the document".
 *
 * The pane preview and the .docx/copy export both render from this, so they
 * cannot drift: what you read is exactly what you download.
 *
 * Two rules the previous field-by-field assembly broke:
 *
 *  1. COMPLETE. Every part of the typed payload is emitted, in payload order.
 *     The old `memoHtml` handled `executive_summary` and `detailed_analysis`
 *     and silently dropped authority_integration, jurisdictional_analysis,
 *     conflicting_authorities, areas_of_uncertainty_and_development,
 *     gaps_and_limitations and practical_implications — which is why the
 *     export "only got parts of the doc".
 *
 *  2. NO ENGLISH SCAFFOLDING. The memo is written in the session's output
 *     language, so a hardcoded "Executive summary" / "Key points" heading
 *     lands as English text inside a Norwegian document. Headings here come
 *     only from the payload (`section.topic`, `area.area`, the analysis key).
 *     Where a group has no payload-supplied heading, it gets none: the content
 *     stands on its own and the document stays monolingual.
 *
 * Metadata ABOUT the answer — confidence, the uncertainty one-liner, draft
 * parties/placeholders/review notes — is deliberately NOT part of the
 * document. It is chrome the pane shows around the document, where it can be
 * localised to the UI language without contaminating the text.
 */

import { citationNumbers } from './renderers/CitationChip';
import {
  ChronologyPayload,
  DraftPayload,
  MemoPayload,
  ResearchCitation,
  ResearchResponse,
} from './types';

/* ------------------------------------------------------------------ */
/*  Blocks                                                             */
/* ------------------------------------------------------------------ */

export interface DocFootnote {
  /** What the reader sees — a citation string, or a source title. */
  label: string;
  /** Optional gloss (the authority's principle, application notes). */
  detail?: string;
  /** The value handed to `onOpenCitation`. */
  target: string;
}

export type DocBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'paragraph'; text: string; lead?: boolean; footnotes?: number[] }
  | { kind: 'list'; items: string[] }
  /** Pre-rendered markdown — the draft body, kept verbatim. */
  | { kind: 'markdown'; text: string }
  | {
      kind: 'event';
      date: string;
      text: string;
      significance?: string;
      actors?: string[];
      approximate?: boolean;
      footnotes?: number[];
    };

export interface ResearchDocument {
  title: string;
  blocks: DocBlock[];
  footnotes: DocFootnote[];
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const BOILERPLATE = new Set(['', 'n/a', 'none', 'not applicable', 'unknown', '-']);

/** Substantive text only — the engine emits "N/A" placeholders for empty slots. */
function meaningful(value: unknown): string {
  const text = String(value ?? '').trim();
  return BOILERPLATE.has(text.toLowerCase()) ? '' : text;
}

/**
 * A heading for an analysis section.
 *
 * `topic` is model-written, so it is already in the document's language. The
 * snake_case key is the fallback — also model-chosen, not our copy.
 */
function sectionHeading(key: string, topic?: string): string {
  const fromPayload = meaningful(topic);
  if (fromPayload) return fromPayload;
  const humanised = key.replace(/_/g, ' ').trim();
  return humanised ? humanised.charAt(0).toUpperCase() + humanised.slice(1) : '';
}

/** A footnote registry that dedupes and numbers by first appearance. */
class Footnotes {
  private readonly order: DocFootnote[] = [];
  private readonly index = new Map<string, number>();

  /** Returns the 1-based number, or 0 when there is nothing to register. */
  add(label: string, detail?: string, target?: string): number {
    const key = String(label ?? '').trim();
    if (!key) return 0;
    const existing = this.index.get(key);
    if (existing) {
      const note = this.order[existing - 1];
      if (!note.detail && detail) note.detail = detail;
      return existing;
    }
    this.order.push({ label: key, detail: meaningful(detail) || undefined, target: target || key });
    const n = this.order.length;
    this.index.set(key, n);
    return n;
  }

  list(): DocFootnote[] {
    return this.order;
  }
}

/** A readable label for a retrieved source. */
function citationLabel(citation: ResearchCitation): string {
  return citation.title || citation.file_name || citation.law || `[${citation.number}]`;
}

/* ------------------------------------------------------------------ */
/*  Memo                                                               */
/* ------------------------------------------------------------------ */

function buildMemo(payload: MemoPayload, footnotes: Footnotes): DocBlock[] {
  const blocks: DocBlock[] = [];

  /* Executive summary — the lead, with no injected heading. */
  const es = payload.executive_summary;
  const directAnswer = meaningful(es?.direct_answer);
  if (directAnswer) blocks.push({ kind: 'paragraph', text: directAnswer, lead: true });

  const findings = (es?.key_findings || []).map(meaningful).filter(Boolean);
  if (findings.length) blocks.push({ kind: 'list', items: findings });

  /* Detailed analysis — one heading + body per section, every section. */
  Object.entries(payload.detailed_analysis || {}).forEach(([key, section]) => {
    if (!section || typeof section !== 'object') return;
    const heading = sectionHeading(key, section.topic);
    const content = meaningful(section.content);
    // A section with a heading but no prose still belongs in the document —
    // dropping it is how the old assembly lost content.
    if (!heading && !content) return;
    if (heading) blocks.push({ kind: 'heading', text: heading });

    const numbers = (section.key_authorities || [])
      .map((auth) =>
        footnotes.add(
          auth.citation,
          [meaningful(auth.principle), meaningful(auth.application_notes)]
            .filter(Boolean)
            .join(' — '),
        )
      )
      .filter((n) => n > 0);

    if (content) {
      blocks.push({ kind: 'paragraph', text: content, footnotes: numbers });
    } else if (numbers.length) {
      blocks.push({ kind: 'paragraph', text: '', footnotes: numbers });
    }
  });

  /* Authority integration — the summaries are prose; the citations are notes. */
  const ai = payload.authority_integration;
  ([ai?.binding_authorities, ai?.persuasive_authorities] as const).forEach((group) => {
    const summary = meaningful(group?.summary);
    const numbers = (group?.citations || [])
      .map((citation) => footnotes.add(citation))
      .filter((n) => n > 0);
    if (summary) blocks.push({ kind: 'paragraph', text: summary, footnotes: numbers });
  });

  /* Jurisdictional analysis — the jurisdiction itself is the heading. */
  const ja = payload.jurisdictional_analysis;
  if (ja) {
    const jurisdiction = meaningful(ja.primary_jurisdiction);
    const frameworks = meaningful(ja.applicable_frameworks);
    const variations = meaningful(ja.variations_by_jurisdiction);
    const federal = meaningful(ja.federal_vs_state);
    if (frameworks || variations || federal) {
      if (jurisdiction) blocks.push({ kind: 'heading', text: jurisdiction });
      [frameworks, variations, federal].filter(Boolean).forEach((text) =>
        blocks.push({ kind: 'paragraph', text })
      );
    }
  }

  /* Conflicting authorities. */
  const conflicts = (payload.conflicting_authorities || [])
    .map((entry) => meaningful(typeof entry === 'string' ? entry : entry?.description))
    .filter(Boolean);
  if (conflicts.length) blocks.push({ kind: 'list', items: conflicts });

  /* Areas of uncertainty — each area names its own heading. */
  (payload.areas_of_uncertainty_and_development || []).forEach((area) => {
    if (!area) return;
    const name = meaningful(area.area);
    const status = meaningful(area.current_status);
    const implications = meaningful(area.implications);
    if (!name && !status && !implications) return;
    if (name) blocks.push({ kind: 'heading', text: name });
    const numbers = (area.relevant_authorities || [])
      .map((citation) => footnotes.add(citation))
      .filter((n) => n > 0);
    if (status) blocks.push({ kind: 'paragraph', text: status, footnotes: numbers });
    if (implications) blocks.push({ kind: 'paragraph', text: implications });
  });

  /* Gaps and limitations. */
  const gaps = payload.gaps_and_limitations;
  [gaps?.information_gaps, gaps?.limitation_summary, gaps?.recommendations_for_deeper_research]
    .map(meaningful)
    .filter(Boolean)
    .forEach((text) => blocks.push({ kind: 'paragraph', text }));

  /* Practical implications — the keys are model-written, so they can head. */
  Object.entries(payload.practical_implications || {}).forEach(([key, value]) => {
    const text = meaningful(value);
    if (!text) return;
    const heading = sectionHeading(key);
    if (heading) blocks.push({ kind: 'heading', text: heading });
    blocks.push({ kind: 'paragraph', text });
  });

  return blocks;
}

/* ------------------------------------------------------------------ */
/*  Draft                                                              */
/* ------------------------------------------------------------------ */

/**
 * The draft document is the instrument: `body_markdown`, entire and verbatim.
 *
 * Parties, governing law, placeholders and assumptions are annotations about
 * the instrument, not clauses in it — they carry labels we would have to
 * invent in English, and a user pasting the export into a file does not want
 * them. The pane shows them beside the document instead.
 */
function buildDraft(payload: DraftPayload, markdown?: string): DocBlock[] {
  const body = payload.body_markdown || markdown || '';
  return body ? [{ kind: 'markdown', text: body }] : [];
}

/* ------------------------------------------------------------------ */
/*  Chronology                                                         */
/* ------------------------------------------------------------------ */

function buildChronology(
  payload: ChronologyPayload,
  citations: ResearchCitation[],
  footnotes: Footnotes
): DocBlock[] {
  const blocks: DocBlock[] = [];
  const summary = meaningful(payload.summary);
  if (summary) blocks.push({ kind: 'paragraph', text: summary, lead: true });

  (payload.events || []).forEach((event) => {
    const text = meaningful(event.event || event.title || event.description);
    const date = meaningful(event.date);
    if (!text && !date) return;

    // The event references retrieved sources by their `[N]` number.
    const numbers = citationNumbers(event)
      .map((n) => citations.find((c) => c.number === n))
      .filter((c): c is ResearchCitation => !!c)
      // The verbatim span when the engine located one, the merged excerpt
      // otherwise. Never `asserts`: a footnote in an exported artifact reads as
      // a quotation of the authority, which is exactly what BUG-01 abused.
      .map((c) =>
        footnotes.add(citationLabel(c), c.quote || c.snippet, c.url || citationLabel(c))
      )
      .filter((n) => n > 0);

    blocks.push({
      kind: 'event',
      date,
      text,
      significance: meaningful(event.significance) || undefined,
      actors: (event.actors || []).filter(Boolean),
      approximate: !!event.date_precision && event.date_precision !== 'day',
      footnotes: numbers,
    });
  });

  const gaps = (payload.gaps || []).map(meaningful).filter(Boolean);
  if (gaps.length) blocks.push({ kind: 'list', items: gaps });

  return blocks;
}

/* ------------------------------------------------------------------ */
/*  Entry point                                                        */
/* ------------------------------------------------------------------ */

/**
 * Assemble the document for an artifact response.
 *
 * When the typed payload is unusable the server-rendered markdown is the
 * document — an export is never empty for something that was on screen.
 */
export function buildDocument(response: ResearchResponse, fallbackTitle = ''): ResearchDocument {
  const footnotes = new Footnotes();
  const payload = (response.payload || {}) as Record<string, any>;
  const title = String(payload.title || fallbackTitle || '').trim();

  if (!response.meta.payloadValid) {
    const markdown = response.markdown || '';
    return {
      title,
      blocks: markdown ? [{ kind: 'markdown', text: markdown }] : [],
      footnotes: [],
    };
  }

  let blocks: DocBlock[];
  switch (response.intent) {
    case 'memo':
    case 'deep_research':
      blocks = buildMemo(payload as MemoPayload, footnotes);
      break;
    case 'draft':
      blocks = buildDraft(payload as DraftPayload, response.markdown);
      break;
    case 'chronology':
      blocks = buildChronology(payload as ChronologyPayload, response.citations, footnotes);
      break;
    default:
      blocks = response.markdown ? [{ kind: 'markdown', text: response.markdown }] : [];
  }

  // A typed payload that yields nothing renderable still has to produce a
  // document — fall back rather than hand back an empty page.
  if (blocks.length === 0 && response.markdown) {
    blocks = [{ kind: 'markdown', text: response.markdown }];
  }

  return { title, blocks, footnotes: footnotes.list() };
}

/**
 * Metadata ABOUT the answer, deliberately kept out of the document.
 *
 * Confidence and the uncertainty one-liner describe how much to trust the
 * memo; they are not part of it. Keeping them out is what stops an English
 * "High confidence" label from landing inside a Norwegian document — the pane
 * shows them as chrome, where the UI language is the right language.
 */
export function answerMetadata(
  response: ResearchResponse
): { confidence: string; uncertainty: string } | null {
  if (response.intent !== 'memo' && response.intent !== 'deep_research') return null;
  if (!response.meta.payloadValid) return null;
  const es = (response.payload as MemoPayload).executive_summary;
  const confidence = meaningful(es?.confidence_level);
  const uncertainty = meaningful(es?.areas_of_uncertainty);
  if (!confidence && !uncertainty) return null;
  return { confidence, uncertainty };
}

/** Headings in the assembled document — used to assert nothing was dropped. */
export function documentHeadings(doc: ResearchDocument): string[] {
  return doc.blocks.filter((b) => b.kind === 'heading').map((b) => (b as any).text);
}
